import type { FieldArrayRenderProps } from 'formik';
import type { PriorityDto } from 'shared/dtos/priorities.dto';

import { CreateButton } from 'components/buttons/create-button/create-button';
import { TextareaField } from 'components/fields/textarea-field/textarea-field';
import { EntityPanel } from 'components/panels/entity-panel/entity-panel';
import { ConfirmationModal } from 'components/template/modals/confirmation-modal';
import { PermissionsContext } from 'contexts';
import { FieldArray } from 'formik';
import { useContext } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { getCurrentYearIdFromLS } from 'shared/utils';
import { useAppDispatch, useAppSelector } from 'store';
import { setSelectedAgencies } from 'store/agencies/agencies.slice';
import { vocabulary } from 'vocabulary/german';

interface MidTermGoalsFormProps {
  priorityIndex: number;
  priority: PriorityDto;
  clearSelectedAgency?: (
    priorityIndex: number,
    midTermGoalIndex: number,
  ) => void;
  handleAssignAgencies: (assignedTo: any) => void;
}

export const MidTermGoalsForm = ({
  priorityIndex,
  priority,
  clearSelectedAgency,
  handleAssignAgencies,
}: MidTermGoalsFormProps) => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const agencies = useAppSelector((state) => state.agencies);
  const isDeletable = permissions.removeFieldsInside === true;

  const removeMidTermGoal = (
    MidtermGoalsHelper: FieldArrayRenderProps,
    midTermGoalIndex: number,
    priorityIndex: number,
  ) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmationModal
            onClose={onClose}
            onOk={() => {
              MidtermGoalsHelper.remove(midTermGoalIndex);
              clearSelectedAgency?.(priorityIndex, midTermGoalIndex);
            }}
          />
        );
      },
    });
  };

  const haveAgenciesSelected = (
    priorityIndex: number,
    midTermGoalIndex: number,
  ) => {
    return agencies.assigned.find(
      (item) =>
        item.assignedTo.priorityIndex === priorityIndex &&
        item.assignedTo.midTermGoalIndex === midTermGoalIndex,
    );
  };

  const removeSelectedAssigning = (
    priorityIndex: number,
    midTermGoalIndex: number,
    selectedAgencyId,
  ) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmationModal
            onClose={onClose}
            onOk={() =>
              handleRemoveSelectedAssigning(
                priorityIndex,
                midTermGoalIndex,
                selectedAgencyId,
              )
            }
          />
        );
      },
    });
  };

  const handleRemoveSelectedAssigning = (
    priorityIndex: number,
    midTermGoalIndex: number,
    selectedAgencyId,
  ) => {
    let newSelectedAgencies: Array<number> = [];

    if (agencies && agencies.assigned) {
      for (const agency of agencies.assigned) {
        if (
          agency.assignedTo.priorityIndex === priorityIndex &&
          agency.assignedTo.midTermGoalIndex === midTermGoalIndex
        ) {
          newSelectedAgencies = agency.selectedAgencies;
        }
      }

      newSelectedAgencies = newSelectedAgencies.filter(
        (id) => id !== selectedAgencyId,
      );

      const selectedAgencies = {
        assignedTo: {
          priorityIndex: priorityIndex,
          midTermGoalIndex: midTermGoalIndex,
        },
        selectedAgencies: newSelectedAgencies,
      };

      dispatch(setSelectedAgencies(selectedAgencies));
    }
  };

  const getAgencyName = (id: number) => {
    const agency = agencies.list.find((agency) => agency.id === id);
    return agency && agency.title ? agency.title : '';
  };

  const removeAssignedAgency = (agencyIndex: number, agenciesHelper) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmationModal
            onClose={onClose}
            onOk={() => agenciesHelper.remove(agencyIndex)}
          />
        );
      },
    });
  };

  return (
    <FieldArray
      name={`Priorities.${priorityIndex}.MidtermGoals`}
      render={(MidtermGoalsHelper) => (
        <div className={'mid-term-goals'}>
          {priority.MidtermGoals && priority.MidtermGoals.length > 0 ? (
            priority.MidtermGoals.map((midTermGoal, midTermGoalIndex) => (
              <div className={'mid-term-goals-item'} key={midTermGoalIndex}>
                <div className={'mid-term-goals-item-content'}>
                  {permissions.removeFieldsInside === true && (
                    <button
                      type="button"
                      data-controlled-permissions={MidTermGoalsForm.name}
                      className={'mid-term-goals-item__remove-icon'}
                      onClick={() =>
                        removeMidTermGoal(
                          MidtermGoalsHelper,
                          midTermGoalIndex,
                          priorityIndex,
                        )
                      }
                    />
                  )}
                  <div className={'form-area-group'}>
                    <TextareaField
                      wrapperClass={'percent-30'}
                      name={`Priorities.${priorityIndex}.MidtermGoals.${midTermGoalIndex}.title`}
                      placeholder={
                        vocabulary.forms.policy.placeholders.newMtgTitle
                      }
                    />
                    <TextareaField
                      wrapperClass={'percent-70'}
                      name={`Priorities.${priorityIndex}.MidtermGoals.${midTermGoalIndex}.description`}
                      placeholder={
                        vocabulary.forms.policy.placeholders.newMtgDescr
                      }
                    />
                  </div>
                </div>
                <div className={'agencies'}>
                  <div className={'agencies-content'}>
                    <FieldArray
                      name={`Priorities.${priorityIndex}.MidtermGoals.${midTermGoalIndex}.Agencies`}
                      render={(agenciesHelper) => (
                        <div className={'agencies-list-wrapper'}>
                          {agencies &&
                            (haveAgenciesSelected(
                              priorityIndex,
                              midTermGoalIndex,
                            ) ? (
                              <div className={'agencies-list'}>
                                {agencies.assigned.map((item) => {
                                  if (
                                    item.assignedTo.priorityIndex ===
                                      priorityIndex &&
                                    item.assignedTo.midTermGoalIndex ===
                                      midTermGoalIndex
                                  ) {
                                    return item.selectedAgencies.map(
                                      (selectedAgencyId) => {
                                        return (
                                          <EntityPanel
                                            isDeletable={isDeletable}
                                            onDelete={() =>
                                              removeSelectedAssigning(
                                                priorityIndex,
                                                midTermGoalIndex,
                                                selectedAgencyId,
                                              )
                                            }
                                            isSmall={true}
                                            id={selectedAgencyId}
                                            title={getAgencyName(
                                              selectedAgencyId,
                                            )}
                                            key={selectedAgencyId}
                                          />
                                        );
                                      },
                                    );
                                  } else {
                                    return '';
                                  }
                                })}
                              </div>
                            ) : midTermGoal.Agencies &&
                              midTermGoal.Agencies.length > 0 ? (
                              midTermGoal.Agencies.map(
                                (agency, agencyIndex) => (
                                  <div
                                    className={'agencies-list'}
                                    key={agencyIndex}
                                  >
                                    <EntityPanel
                                      isDeletable={isDeletable}
                                      onDelete={() =>
                                        removeAssignedAgency(
                                          agencyIndex,
                                          agenciesHelper,
                                        )
                                      }
                                      isSmall={true}
                                      id={agency.id}
                                      title={agency.title}
                                    />
                                  </div>
                                ),
                              )
                            ) : (
                              <></>
                            ))}
                          {permissions.assign === true && (
                            <CreateButton
                              text={vocabulary.buttons.assignAgencies}
                              isSmall
                              noWrapper
                              onClick={() => {
                                handleAssignAgencies({
                                  priorityIndex: priorityIndex,
                                  midTermGoalIndex: midTermGoalIndex,
                                  agencies: midTermGoal.Agencies,
                                });
                              }}
                            />
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <></>
          )}
          <>
            {permissions.addFieldsInside === true && (
              <CreateButton
                noWrapper
                text={vocabulary.buttons.addMidTermGoal}
                onClick={() => {
                  MidtermGoalsHelper.push({
                    YearId: getCurrentYearIdFromLS(),
                    title: '',
                    description: '',
                  });
                }}
              />
            )}
          </>
        </div>
      )}
    />
  );
};
